<script setup>
import { computed, onBeforeUnmount, ref } from "vue";
import { projects } from "../data/content";
import IconArrow from "./IconArrow.vue";

const props = defineProps({
  locale: { type: String, required: true },
  copy: { type: Object, required: true },
});

const controls = {
  es: { previous: "Proyecto anterior", next: "Proyecto siguiente", selection: "Selección de proyectos" },
  en: { previous: "Previous project", next: "Next project", selection: "Project selection" },
  it: { previous: "Progetto precedente", next: "Progetto successivo", selection: "Selezione progetti" },
};

const track = ref(null);
const active = ref(0);
let frame;
const labels = computed(() => controls[props.locale]);

function moveTo(index) {
  const next = (index + projects.length) % projects.length;
  const slide = track.value?.children.item(next);
  if (!track.value || !slide) return;
  track.value.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
  active.value = next;
}

function syncActive() {
  if (frame) cancelAnimationFrame(frame);
  frame = requestAnimationFrame(() => {
    if (!track.value) return;
    const slides = Array.from(track.value.children);
    const closest = slides.reduce(
      (best, slide, index) => {
        const distance = Math.abs(slide.offsetLeft - track.value.scrollLeft);
        return distance < best.distance ? { index, distance } : best;
      },
      { index: 0, distance: Number.POSITIVE_INFINITY },
    );
    active.value = closest.index;
  });
}

onBeforeUnmount(() => frame && cancelAnimationFrame(frame));
</script>

<template>
  <div class="projects-swiper" :aria-label="labels.selection">
    <div ref="track" class="projects-swiper__track" @scroll.passive="syncActive">
      <article v-for="(project, index) in projects" :key="project.title" class="project-slide">
        <div class="project-slide__image">
          <img :src="project.image" alt="" width="960" height="600" loading="lazy">
          <div class="project-slide__veil" />
          <span class="project-slide__number">{{ String(index + 1).padStart(2, "0") }}</span>
          <span class="project-slide__category">{{ project.category }}</span>
        </div>
        <div class="project-slide__body">
          <div>
            <h3>{{ project.title }}</h3>
            <p>{{ project.description[locale] }}</p>
          </div>
          <a
            v-if="project.href"
            class="round-link"
            :href="project.href"
            target="_blank"
            rel="noreferrer"
            :aria-label="`${copy.view}: ${project.title}`"
            data-analytics="project_visit"
            :data-analytics-label="project.title"
          >
            <IconArrow />
          </a>
          <span v-else class="project-private">{{ copy.unavailable }}</span>
        </div>
        <div class="project-slide__pediment" aria-hidden="true"><span /></div>
      </article>
    </div>

    <div class="projects-swiper__controls">
      <div class="projects-swiper__counter" aria-live="polite">
        <strong>{{ String(active + 1).padStart(2, "0") }}</strong>
        <span />
        <small>{{ String(projects.length).padStart(2, "0") }}</small>
      </div>
      <div class="projects-swiper__progress" aria-hidden="true">
        <span :style="{ transform: `scaleX(${(active + 1) / projects.length})` }" />
      </div>
      <div class="projects-swiper__buttons">
        <button type="button" :aria-label="labels.previous" @click="moveTo(active - 1)">←</button>
        <button type="button" :aria-label="labels.next" @click="moveTo(active + 1)">→</button>
      </div>
    </div>
  </div>
</template>
